// `extension-education-types-mapping.service.ng1.ts` — the Education section's three columns.
//
// ⚠️ THESE ARE STATIC, NOT BOOTSTRAP DATA, and believing otherwise cost the section eight of
// its eleven checkboxes. `EditAlert` passed `degreeGroups={[]} fieldGroups={[]}
// instituteBadges={[]}` under a note saying they "come from `window.careero.degrees` and
// friends upstream" and were "not on the bootstrap yet". They do not: the service declares
// them as literals in its own body (lines 29-71) and only `educationType` — which this
// section does not use — is read from `$window`. So the column rendered its "Any" row alone
// while production showed the degree levels, the fields of study and the school tiers.
//
// Measured against build 354 on the edit-alert screen: 21 visible checkboxes there, 13 here.

/** `IEducationGroup` — a display group folding one or more wire values. */
export interface EducationGroupData {
  key: string
  values: string[]
}

/**
 * `degreeGroups` (service:29).
 *
 * ⚠️ `values` ARE THE MAPPING'S KEYS, NOT ITS LABELS. Upstream writes
 * `getKeyByValue(degreeType.ba, degreeType)`, which looks up `'B.A'` and hands back `'ba'` —
 * a round trip that reads as a label and resolves to a key. The saved alert stores those
 * keys, so writing the labels here would make every loaded alert look empty.
 */
export const DEGREE_GROUPS: readonly EducationGroupData[] = [
  { key: 'Any degree', values: ['any'] },
  { key: 'BA / BsC', values: ['ba', 'bsc'] },
  { key: 'MA / MsC / MBA', values: ['ma', 'msc', 'mba'] },
  { key: 'PhD', values: ['phd'] },
]

/** `fieldGroups` (service:51). */
export const FIELD_GROUPS: readonly EducationGroupData[] = [
  // ⚠️ "Any field of STUDY" — the port's own label read "Any field", which is a different
  // string in a 200px column where the copy is the whole affordance.
  { key: 'Any field of study', values: ['ANY'] },
  { key: 'Software / Computer science', values: ['SOFTWARE', 'COMPUTER_SCIENCE'] },
  { key: 'Sciences', values: ['SCIENCES'] },
  { key: 'Creative arts', values: ['CREATIVE_ARTS'] },
]

/**
 * `mappingToMappingDisplay(getInstituteBadgeNoOtherMapping())` — `instituteBadgeNoOther`, in
 * its declared order.
 *
 * ⚠️ THE "NO OTHER" MAPPING, NOT `instituteBadge`. The other one carries an `OTHER: 'Other'`
 * row and different labels ('Elite', 'Top Tier'); this section is given the one without it.
 */
export const INSTITUTE_BADGES: readonly { key: string; text: string }[] = [
  { key: 'ELITE_SCHOOL', text: 'Ivy league school' },
  { key: 'TOP_TIER_SCHOOL', text: 'Top Tier school' },
]

/** `getAnyDegreeGroup()` — the group whose values contain `'any'`. */
export const ANY_DEGREE_GROUP = DEGREE_GROUPS.find((g) => g.values.includes('any')) as EducationGroupData

/** `getAnyFieldGroup()` — the group whose values contain `'ANY'`. Note the casing. */
export const ANY_FIELD_GROUP = FIELD_GROUPS.find((g) => g.values.includes('ANY')) as EducationGroupData

/** `degreeGroupsForDisplay` / `fieldGroupsForDisplay` — the groups MINUS the "Any" row. */
export const DEGREE_GROUPS_FOR_DISPLAY = DEGREE_GROUPS.filter((g) => g.key !== ANY_DEGREE_GROUP.key)
export const FIELD_GROUPS_FOR_DISPLAY = FIELD_GROUPS.filter((g) => g.key !== ANY_FIELD_GROUP.key)
